import { createContext, type ReactNode, useMemo } from 'react';

import { getEndpointUrl } from 'api';
import { useRequiredContext } from 'hooks';
import { usePagedData } from 'hooks/data-fetching';
import type { ProductQuestionsResponse, Question } from 'models/api';
import type { VariantId } from 'models/product';

interface ProductQuestionsContextType {
	hasNextPage: boolean;
	isLoading: boolean;
	isLoadingMore: boolean;
	loadMore: () => void;
	newQuestionsAllowed: boolean;
	questions: Question[] | undefined;
}

export const ProductQuestionsContext = createContext<
	ProductQuestionsContextType | undefined
>(undefined);

interface Props {
	children: ReactNode;
	isActive: boolean;
	variantId: VariantId;
}

export function ProductQuestionsContextProvider({
	children,
	isActive,
	variantId,
}: Props) {
	const {
		hasNextPage,
		isLoading,
		isLoadingMore,
		items,
		loadMore,
		newQuestionsAllowed,
	} = usePagedData<ProductQuestionsResponse, 'questions'>({
		baseUrl:
			isActive && variantId
				? getEndpointUrl({
						name: 'GetProductQuestions',
						pathParams: {
							variantId,
						},
					})
				: null,
		itemsKey: 'questions',
	});

	const value = useMemo(
		() => ({
			hasNextPage,
			isLoading,
			isLoadingMore: Boolean(isLoadingMore),
			questions: items,
			loadMore,
			newQuestionsAllowed: Boolean(newQuestionsAllowed),
		}),
		[
			hasNextPage,
			isLoading,
			isLoadingMore,
			items,
			loadMore,
			newQuestionsAllowed,
		],
	);

	return (
		<ProductQuestionsContext value={value}>{children}</ProductQuestionsContext>
	);
}
ProductQuestionsContextProvider.displayName = 'ProductQuestionsContextProvider';

export function useProductQuestionsContext() {
	return useRequiredContext(ProductQuestionsContext, 'ProductQuestionsContext');
}
